import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import VinculoDialog from '../componentes/VinculoDialog'
import DarAcessoDialog from '../componentes/DarAcessoDialog'
import { plural, SEM_FICHA } from './comum'
import { EquipeResumo, Situacao } from './Pessoas'
import './gestao.scss'

export interface DashboardComAcesso { vinculoId: number; dashboardId: number; dashboard: string; acessoDiretoId?: number | null; equipes: string[] }
export interface AcessosNoAmbiente { ambienteId: number; ambiente: string; dashboards: DashboardComAcesso[] }
interface Detalhe {
  id: number; nome: string; username: string; email: string; empresa?: string | null; interna: boolean; ativa: boolean; bloqueada: boolean; telefone?: string | null
  equipes: EquipeResumo[]; acessos: AcessosNoAmbiente[]
  regras: { itemId: number; regraId: number; regra: string; atributo: string; valor: string; dashboard?: string | null; ambiente?: string | null }[]
  oneflow: { agenteId: number; agente: string; limite?: number | null }[]
}

const AUTHMANAGER = 'https://admin.tradetalentos.com.br'

/** Tabela de acessos por ambiente (pessoa ou equipe): direto pode ser tirado aqui; pela equipe, só na equipe. */
export function TabelaAcessos({ acessos, aoTirar, rotuloDireto = 'direto' }: { acessos: AcessosNoAmbiente[]; aoTirar: (d: DashboardComAcesso) => void; rotuloDireto?: string }) {
  const [busca, setBusca] = useState('')
  const q = busca.trim().toLowerCase()
  const total = acessos.reduce((s, a) => s + a.dashboards.length, 0)
  const linhas = acessos
    .map(a => ({ ...a, dashboards: a.dashboards.filter(d => !q || `${a.ambiente} ${d.dashboard}`.toLowerCase().includes(q)) }))
    .filter(a => a.dashboards.length)
  return (
    <>
      {total > 10 && (
        <div className="ng-filtros" style={{ marginBottom: 8 }}>
          <label className="ng-busca"><SearchIcon fontSize="small" />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar ambiente ou dashboard" aria-label="Buscar acesso" />
          </label>
        </div>
      )}
      <div className="ng-tabela">
        <table>
          <thead><tr><th>Ambiente</th><th>Dashboards</th></tr></thead>
          <tbody>
            {total === 0 && <tr><td colSpan={2} className="vazio">Nenhum acesso ainda.</td></tr>}
            {linhas.map(a => (
              <tr key={a.ambienteId}>
                <td style={{ width: 220, verticalAlign: 'top' }}><Link to={`/app/gestao/ambientes/${a.ambienteId}`}><b>{a.ambiente}</b></Link></td>
                <td>
                  <div className="ng-chips">
                    {a.dashboards.map(d => (
                      <span key={d.vinculoId} className="ng-chip">
                        <span><b><Link to={`/app/gestao/dashboards/${d.dashboardId}/ambiente/${a.ambienteId}`}>{d.dashboard}</Link></b>
                          <small>{[d.acessoDiretoId ? rotuloDireto : '', d.equipes.length ? `pela equipe ${d.equipes.join(', ')}` : ''].filter(Boolean).join(' e ')}</small></span>
                        {d.acessoDiretoId && <button type="button" className="ng-ico perigo" onClick={() => aoTirar(d)} aria-label={`Tirar o acesso a ${d.dashboard}`} title="Tirar o acesso"><CloseIcon fontSize="small" /></button>}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** Gestão › Pessoas › uma pessoa. */
export default function Pessoa() {
  const { id } = useParams()
  const { notify } = useNotification()
  const [p, setP] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [todasEquipes, setTodasEquipes] = useState<EquipeResumo[]>([])
  const [addEquipes, setAddEquipes] = useState(false)
  const [darAcesso, setDarAcesso] = useState(false)

  const carregar = useCallback(async () => {
    const r = await services.api.novoPortalPessoa(id)
    if (r.fail) { setErro(r.status === 403 ? SEM_FICHA : r.error || 'Pessoa não encontrada.'); return }
    setP(r.data)
  }, [id])

  useEffect(() => { carregar() }, [carregar])
  useEffect(() => { services.api.novoPortalEquipes().then((r: any) => { if (!r.fail) setTodasEquipes(r.data || []) }) }, [])

  if (erro) return <div className="ng"><p className="ng-explic">{erro}</p><Link to="/app/gestao/pessoas">Voltar a Pessoas</Link></div>
  if (!p) return <div className="ng"><p className="ng-explic">Carregando…</p></div>

  const feito = async (acao: Promise<any>, ok: string, falha: string) => {
    const r = await acao
    if (r.fail) { notify(r.error || falha, { variant: 'error' }); return }
    notify(ok, { variant: 'success' })
    carregar()
  }

  const tirarEquipe = (e: EquipeResumo) => feito(services.api.removeUserFromTeam(e.id, p.id), `${p.nome} saiu da equipe ${e.nome}.`, 'Não foi possível tirar da equipe.')
  const tirarAcesso = (d: DashboardComAcesso) => feito(services.api.removeUserFromDashboardGroup(d.acessoDiretoId), `Acesso direto a ${d.dashboard} retirado.`, 'Não foi possível tirar o acesso.')
  const tirarItem = (i: Detalhe['regras'][number]) => feito(services.api.deleteDashboardFilterRule(i.itemId), `Item ${[i.atributo, i.valor].filter(Boolean).join(' · ')} retirado da regra ${i.regra}.`, 'Não foi possível remover o item.')
  const vinculos = p.acessos.flatMap(a => a.dashboards.filter(d => d.acessoDiretoId).map(d => d.vinculoId))
  const totalDash = p.acessos.reduce((s, a) => s + a.dashboards.length, 0)

  return (
    <div className="ng">
      <nav className="ng-trilha" aria-label="Caminho"><Link to="/app/gestao/pessoas">Pessoas</Link><span>›</span><span>{p.nome}</span></nav>
      <div className="ng-cab">
        <h1>{p.nome} <Situacao p={p} /></h1>
        <div className="ng-acoes"><a className="ng-btn" href={AUTHMANAGER} target="_blank" rel="noreferrer">Login e senha no AuthManager</a></div>
      </div>

      <div className="ng-duas">
        <section className="ng-cartao">
          <h2>Dados</h2>
          <p className="ng-explic">Vêm do AuthManager. Para mudar nome, e-mail, senha ou situação, use o AuthManager.</p>
          <dl className="ng-dados">
            <dt>E-mail</dt><dd>{p.email}</dd>
            <dt>Login</dt><dd>{p.username}</dd>
            <dt>Empresa</dt><dd>{p.empresa || '—'} <span className="ng-tag">{p.interna ? 'Interna' : 'Cliente'}</span></dd>
            <dt>Telefone</dt><dd>{p.telefone || '—'}</dd>
          </dl>
        </section>

        <section className="ng-cartao">
          <div className="ng-cartao-cab">
            <h2>Equipes ({p.equipes.length})</h2>
            <div className="ng-acoes"><button type="button" className="ng-btn" onClick={() => setAddEquipes(true)}><AddIcon />Adicionar</button></div>
          </div>
          <p className="ng-explic">A pessoa vê tudo o que as equipes dela veem.</p>
          <div className="ng-lista">
            {p.equipes.length === 0 && <p className="ng-explic">Em nenhuma equipe.</p>}
            {p.equipes.map(e => (
              <div key={e.id} className="ng-linha">
                <span className="ng-amb-ini">{e.nome.charAt(0)}</span>
                <span className="ng-amb-txt"><Link to={`/app/gestao/equipes/${e.id}`}><b>{e.nome}</b></Link><small>{plural(e.membros, 'pessoa', 'pessoas')} · {plural(e.dashboards, 'dashboard', 'dashboards')}</small></span>
                <button type="button" className="ng-ico perigo" onClick={() => tirarEquipe(e)} aria-label={`Tirar ${p.nome} da equipe ${e.nome}`} title="Tirar da equipe"><CloseIcon fontSize="small" /></button>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Acessos ({plural(totalDash, 'dashboard', 'dashboards')})</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => setDarAcesso(true)}><AddIcon />Dar acesso</button></div>
        </div>
        <p className="ng-explic">Direto ou pela equipe. O acesso pela equipe só sai tirando a pessoa da equipe (ou o acesso da equipe). Administradores do BI veem tudo, além do que está aqui.</p>
        <TabelaAcessos acessos={p.acessos} aoTirar={tirarAcesso} />
      </section>

      <section className="ng-cartao">
        <h2>Regras</h2>
        <p className="ng-explic">O recorte que a pessoa vê nos dashboards com regra. Sem valor numa regra, os dashboards que a usam abrem vazios.</p>
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Regra</th><th>Atributo</th><th>Valor</th><th>Vale para</th><th /></tr></thead>
            <tbody>
              {p.regras.length === 0 && <tr><td colSpan={5} className="vazio">Sem valor em nenhuma regra.</td></tr>}
              {p.regras.map(i => (
                <tr key={i.itemId}>
                  <td><Link to={`/app/gestao/regras/${i.regraId}`}>{i.regra}</Link></td>
                  <td>{i.atributo ? <span className="ng-tag">{i.atributo}</span> : <span style={{ color: '#8b92a1' }}>—</span>}</td>
                  <td><span className="ng-tag">{i.valor}</span></td>
                  <td>{i.dashboard ? `${i.dashboard} em ${i.ambiente}` : 'Todos os dashboards'}</td>
                  <td style={{ textAlign: 'right' }}><button type="button" className="ng-ico perigo" onClick={() => tirarItem(i)} aria-label={`Remover o valor ${i.valor}`}><CloseIcon fontSize="small" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ng-cartao">
        <h2><span className="ng-oneflow"><b>one</b><i>flow</i> IA</span></h2>
        <p className="ng-explic">Agentes em que a pessoa está liberada. Liberar e tirar fica na página do agente.</p>
        {p.oneflow.length === 0
          ? <p className="ng-explic">Não está liberada em nenhum agente.</p>
          : <div className="ng-chips">{p.oneflow.map(a => (
            <Link key={a.agenteId} className="ng-chip" to={`/app/gestao/agentes/${a.agenteId}`}><b>{a.agente}</b><small>{a.limite ? `até ${a.limite.toLocaleString('pt-BR')} tokens/mês` : 'sem limite individual'}</small></Link>
          ))}</div>}
      </section>

      <VinculoDialog
        aberto={addEquipes}
        titulo="Adicionar a equipes"
        sub={p.nome}
        abas={[{
          id: 'e', rotulo: 'Equipes', dica: 'Buscar equipe', unidade: ['equipe', 'equipes'],
          itens: todasEquipes.map(e => ({ id: String(e.id), nome: e.nome, sub: plural(e.membros, 'pessoa', 'pessoas'), ja: p.equipes.some(x => x.id === e.id) ? 'Já está' : undefined }))
        }]}
        resumo={(s, total) => total ? <><b>{p.nome}</b> entra em <b>{plural(total, 'equipe', 'equipes')}</b> e passa a ver o que elas veem.</> : <>Quem entra numa equipe vê todos os dashboards dela.</>}
        botao={total => total ? `Adicionar a ${plural(total, 'equipe', 'equipes')}` : 'Adicionar'}
        aoFechar={() => setAddEquipes(false)}
        aoConfirmar={async s => {
          const r = await services.api.vicularUsuarioComEquipeMult((s.e || []).map(e => ({ teamId: Number(e.id), userId: p.id })))
          if (r.fail) return r.error || 'Não foi possível adicionar às equipes.'
          setAddEquipes(false)
          notify(`${p.nome} entrou nas equipes.`, { variant: 'success' })
          carregar()
          return null
        }}
      />

      <DarAcessoDialog aberto={darAcesso} pessoas={[p.id]} quem={p.nome} ja={vinculos} aoFechar={() => setDarAcesso(false)}
                       aoDar={m => { setDarAcesso(false); notify(m, { variant: 'success' }); carregar() }} />
    </div>
  )
}
